import { keepPreviousData, useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { FundFamilyDistributionTableContext } from "features/distributions/FundFamilyDistributionTableContext";
import { DrawdownFund } from "features/drawdown/DrawdownFund";
import { GetAllocationsByLookupKeyFn } from "features/drawdown/DrawdownUtils";
import { feeDistributionAllocationDetailsRetrieve } from "gen/endpoints/distributions/distributions";
import { AllocationLookupKey } from "gen/model/allocationLookupKey";
import { FeeDistributionAllocationByEntity } from "gen/model/feeDistributionAllocationByEntity";
import { FundFamilyUseAllocationTableColumnDataDistribution } from "gen/model/fundFamilyUseAllocationTableColumnDataDistribution";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { ManagementFeeDistributionMethodologyEnum } from "gen/model/managementFeeDistributionMethodologyEnum";
import { memoize } from "lodash-es";
import { useMemo } from "react";
import {
  FundFamilyUseAllocationTableGetAllocationFn,
  FundFamilyUseAllocationTableGetAllocationParams,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const getFundAllocationColumnData = (
  row: FundFamilyUseAllocationTableRow | undefined,
): FundFamilyUseAllocationTableColumnDataDistribution | undefined =>
  row?.column_data?.type === "distribution" ? row.column_data : undefined;

const getFundAllocation = (
  params: FundFamilyUseAllocationTableGetAllocationParams,
  allocationLookupParams: {
    transaction_code_id: string | null;
    investment_id: string | null;
  },
): DrawdownFund | undefined => {
  const { context, fundDef, amountTypeDef } = params;

  // skip the auto updated fields that are read only and not editable (i.e. remaining distribution)
  if (amountTypeDef.is_read_only) {
    return undefined;
  }

  const allocationContext =
    context as unknown as FundFamilyDistributionTableContext;
  // get the right allocations based on whether it's a fee break or normal distribution amount
  const hasFeeUpdate =
    amountTypeDef.meta?.type &&
    amountTypeDef.meta.type in ManagementFeeDistributionMethodologyEnum;
  const allocationsByLookupKey = hasFeeUpdate
    ? allocationContext.getFeeUpdateAllocationsByLookupKey
    : allocationContext.getAllocationsByLookupKey;

  const fundAllocations = allocationsByLookupKey?.(allocationLookupParams);
  return fundAllocations?.find((it) => it.id === fundDef.fund_id);
};

export const getFundAllocationForFundFamilyDistributions: FundFamilyUseAllocationTableGetAllocationFn =
  (params) => {
    const distributionColumnData = getFundAllocationColumnData(params.row);
    const transactionCodeId = distributionColumnData?.transaction_code_id;
    const investmentId = distributionColumnData?.investment_id;
    if (!transactionCodeId && !investmentId) {
      return undefined;
    }
    const allocationLookupParams = {
      transaction_code_id: transactionCodeId ?? null,
      investment_id: investmentId ?? null,
    };

    const fundInfo = getFundAllocation(params, allocationLookupParams);
    if (!fundInfo) {
      return undefined;
    }
    return {
      ownershipPercentage: fundInfo.ownershipPercentage,
      allocationRuleId: fundInfo.allocationRuleId,
      allocationRuleName: fundInfo.allocationRuleName,
    };
  };

export const feeUpdateAllocationByEntityToDrawdownFund = (
  entity: FeeDistributionAllocationByEntity,
): DrawdownFund => ({
  id: entity.entity_id,
  title: "",
  ownershipPercentage: new Decimal(entity.ownership_percentage),
  basisAmount: new Decimal(entity.basis_amount),
});

const feeUpdateAllocationDetailsSearchQueryKey = (
  fundFamilyId: string,
  fundFamilyEventId: string,
  payloadKey: AllocationLookupKey[],
  feeChargeEventIds: string[],
  feeId: string,
) =>
  [
    `/fund-family/${fundFamilyId}/distributions/fund-family-event/${fundFamilyEventId}/fee-break-distribution-allocations`,
    payloadKey,
    feeChargeEventIds,
    feeId,
  ] as const;

export const useGetFeeUpdateAllocationsByLookupKey = (
  lookupKeys: AllocationLookupKey[],
  feeId: string,
  feeChargeEventIds: string[],
  management_fee_distribution_methodology: ManagementFeeDistributionMethodologyEnum,
): GetAllocationsByLookupKeyFn => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  let enabled = lookupKeys.length > 0;
  if (
    management_fee_distribution_methodology ==
    ManagementFeeDistributionMethodologyEnum.fee_holdback
  ) {
    enabled = feeChargeEventIds.length > 0 && lookupKeys.length > 0;
  }
  const { data } = useQuery({
    queryKey: feeUpdateAllocationDetailsSearchQueryKey(
      fundFamilyId,
      fundFamilyEventId,
      lookupKeys,
      feeChargeEventIds,
      feeId,
    ),
    queryFn: async () =>
      feeDistributionAllocationDetailsRetrieve(
        fundFamilyId,
        fundFamilyEventId,
        {
          allocation_lookup_key_list: lookupKeys,
        },
        {
          fee_id: feeId,
          fee_charge_event_ids: feeChargeEventIds,
        },
      ),
    enabled,
    throwOnError: false,
    placeholderData: keepPreviousData,
  });

  const lookupKeyToDrawdownFundDetails = useMemo(() => {
    const result = new Map<string, DrawdownFund[]>();
    data?.forEach(
      ({ allocation_lookup_key, fee_distribution_allocation_by_entity }) => {
        const drawdownFunds: DrawdownFund[] =
          fee_distribution_allocation_by_entity.map(
            feeUpdateAllocationByEntityToDrawdownFund,
          );
        result.set(JSON.stringify(allocation_lookup_key), drawdownFunds);
      },
    );
    return result;
  }, [data]);

  return useMemo(() => {
    if (!lookupKeyToDrawdownFundDetails.size) {
      return undefined;
    }
    return memoize(
      (allocationLookupKey: AllocationLookupKey) =>
        lookupKeyToDrawdownFundDetails.get(
          JSON.stringify(allocationLookupKey),
        ) || [],
    );
  }, [lookupKeyToDrawdownFundDetails]);
};
